import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { Breadcrumbs, StatusBar } from "@/components/layout/Record";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, Field, LoadingState, ErrorState, StatusBadge, ConfirmDialog } from "@/components/common";
import { PlanGate } from "@/components/PlanGate";
import { QuoteDocument, TotalsBlock } from "@/modules/quotes/QuoteDocument";
import { LineRow, toForm, toBody } from "@/modules/quotes/QuoteEditor";
import { PaymentDialog, CreditNoteDialog, VoidDialog } from "@/modules/invoices/InvoiceDialogs";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtCents, fmtDateTime, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const TYPES = ["part", "labour", "other", "discount"];

function DraftEditor({ inv, onSaved }) {
  const { garage } = useGarage();
  const [f, setF] = useState({ ...toForm({ ...inv, valid_until: "" }), notes: inv.notes || "", terms: inv.terms || "" });
  const [preview, setPreview] = useState(inv.totals);
  const up = (p) => setF((x) => ({ ...x, ...p }));
  useEffect(() => {
    const id = setTimeout(() => api.post("/quotes/calc", toBody(f)).then((r) => setPreview(r.data)).catch(() => {}), 300);
    return () => clearTimeout(id);
  }, [f]);
  const save = async () => {
    try {
      const { valid_until, ...body } = toBody(f);
      const { data } = await api.put(`/invoices/${inv.id}`, body);
      toast.success(t("common.saved"));
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const blank = (type) => ({ type, description: "", qty: "1", unit_price: "", discount_pct: "0", cost_price: "" });
  return (
    <Card testId="invoice-editor">
      <SectionTitle hint={t("quotes.linesHint")}>{t("quotes.lines")}</SectionTitle>
      <div className="space-y-2">{f.lines.map((l, i) => <LineRow key={l.id || i} l={l} i={i} types={TYPES} set={(j, p) => up({ lines: f.lines.map((x, k) => (k === j ? { ...x, ...p } : x)) })} remove={(j) => up({ lines: f.lines.filter((_, k) => k !== j) })} />)}</div>
      <div className="mt-3 flex flex-wrap gap-2">{["part", "labour", "other"].map((ty) => <Button key={ty} size="sm" variant="outline" onClick={() => up({ lines: [...f.lines, blank(ty)] })} data-testid={`inv-add-line-${ty}`}><Plus className="mr-1 h-3.5 w-3.5" /> {t(`status.${ty}`)}</Button>)}</div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label={t("quotes.quoteDiscount")}>
          <Select value={f.discount_type} onValueChange={(v) => up({ discount_type: v })}>
            <SelectTrigger data-testid="inv-discount-type"><SelectValue /></SelectTrigger>
            <SelectContent>{["none", "pct", "amount"].map((x) => <SelectItem key={x} value={x}>{t(`quotes.discountTypes.${x}`)}</SelectItem>)}</SelectContent>
          </Select>
          {f.discount_type !== "none" && <input className="mt-2 w-full rounded-md border px-3 py-2 text-sm" type="number" step="0.01" min="0" value={f.discount_value} onChange={(e) => up({ discount_value: e.target.value })} data-testid="inv-discount-value" />}
        </Field>
        <TotalsBlock totals={preview} currency={garage.currency} testId="inv-preview-totals" />
        <Field label={t("quotes.notes")} className="sm:col-span-2"><Textarea value={f.notes} onChange={(e) => up({ notes: e.target.value })} data-testid="inv-notes" /></Field>
        <Field label={t("invoices.paymentTerms")} className="sm:col-span-2"><Textarea value={f.terms} onChange={(e) => up({ terms: e.target.value })} data-testid="inv-terms" /></Field>
      </div>
      <Button className="mt-4" onClick={save} data-testid="invoice-save">{t("quotes.saveDraft")}</Button>
    </Card>
  );
}

function PaymentsCard({ inv }) {
  return (
    <Card testId="invoice-payments">
      <SectionTitle>{t("invoices.payments")}</SectionTitle>
      <dl className="mb-3 space-y-1 text-sm">
        <div className="flex justify-between"><dt>{t("invoices.paidNet")}</dt><dd data-testid="invoice-paid">{fmtCents(inv.paid_cents)}</dd></div>
        <div className="flex justify-between"><dt>{t("invoices.credited")}</dt><dd data-testid="invoice-credited">{fmtCents(inv.credited_cents)}</dd></div>
        <div className="flex justify-between border-t pt-1 font-bold"><dt>{inv.balance_cents < 0 ? t("invoices.refundDue") : t("invoices.balanceDue")}</dt><dd data-testid="invoice-balance">{fmtCents(Math.abs(inv.balance_cents))}</dd></div>
      </dl>
      {(inv.payments || []).length === 0 ? <p className="text-sm text-slate-500">{t("invoices.noPayments")}</p> : (
        <ul className="space-y-2 text-sm">{inv.payments.map((p) => (
          <li key={p.id} className="flex justify-between rounded-lg border border-slate-200 p-2" data-testid={`payment-${p.id}`}>
            <span>{fmtYmd(p.date)} · {t(`invoices.methods.${p.method}`)}{p.reference && ` · ${p.reference}`}<span className="block text-[11px] text-slate-400">{p.recorded_by}</span></span>
            <strong className={p.kind === "refund" ? "text-red-600" : ""}>{p.kind === "refund" ? "−" : ""}{fmtCents(p.amount_cents)}</strong>
          </li>))}</ul>
      )}
      {(inv.credit_notes || []).length > 0 && <div className="mt-4"><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("invoices.creditNotes")}</p>
        {inv.credit_notes.map((c) => <Link key={c.id} to={`/pro/invoices/${c.id}`} className="flex justify-between rounded-lg border border-slate-200 p-2 text-sm" data-testid={`credit-note-${c.number}`}><span className="font-mono">{c.number}</span><span>−{fmtCents(c.total_cents)}</span></Link>)}</div>}
    </Card>
  );
}

function Detail() {
  const { id } = useParams();
  const { garage, readOnly } = useGarage();
  const { data: inv, error, loading, reload } = useFetch(`/invoices/${id}`);
  const [dlg, setDlg] = useState(null);
  const [confirmIssue, setConfirmIssue] = useState(false);
  if (loading && !inv) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const done = () => { setDlg(null); reload(); };
  const issue = async () => {
    setConfirmIssue(false);
    try { await api.post(`/invoices/${inv.id}/issue`, {}); toast.success(t("invoices.issued")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const isCN = inv.kind === "credit_note";
  const live = ["issued", "partially_paid", "paid", "credited"].includes(inv.status);
  const ident = inv.garage || garage;
  const meta = isCN ? [[t("invoices.date"), fmtYmd(inv.issue_date)], [t("invoices.forInvoiceLabel"), inv.invoice_number]]
    : [[t("invoices.issueDate"), inv.issue_date ? fmtYmd(inv.issue_date) : "—"], [t("invoices.dueDate"), inv.due_date ? fmtYmd(inv.due_date) : "—"]];
  return (
    <div className="space-y-6" data-testid="invoice-page">
      <Breadcrumbs testId="invoice-crumbs" items={[[t("b13.nav.sales"), "/pro/invoices"], [t("invoices.title"), isCN ? "/pro/invoices?status=credit_notes" : "/pro/invoices"], [inv.number || t("invoices.draft")]]} />
      {!isCN && <StatusBar steps={["draft", "issued", "partially_paid", ["void", "credited"].includes(inv.status) ? inv.status : "paid"]} current={inv.status === "overdue" ? "issued" : inv.status} />}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="font-mono text-sm font-semibold text-teal-700" data-testid="invoice-number">{inv.number || t("invoices.draft")}</p><h1 className="font-display text-3xl font-extrabold">{inv.customer.name}</h1></div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={inv.status} testId="invoice-status" />
          {inv.status !== "draft" && <Button variant="outline" onClick={() => downloadFile(`/invoices/${inv.id}/pdf`).catch((e) => toast.error(errMsg(e)))} data-testid="invoice-pdf"><Download className="mr-2 h-4 w-4" /> PDF</Button>}
          {!readOnly && inv.status === "draft" && <Button onClick={() => setConfirmIssue(true)} data-testid="invoice-issue">{t("invoices.issue")}</Button>}
          {!readOnly && !isCN && ["issued", "partially_paid"].includes(inv.status) && <Button onClick={() => setDlg("payment")} data-testid="invoice-record-payment">{t("invoices.recordPaymentReceived")}</Button>}
          {!readOnly && !isCN && live && inv.paid_cents > 0 && <Button variant="outline" onClick={() => setDlg("refund")} data-testid="invoice-record-refund">{t("invoices.recordRefund")}</Button>}
          {!readOnly && !isCN && live && inv.credited_cents < inv.totals.total_cents && <Button variant="outline" onClick={() => setDlg("credit")} data-testid="invoice-credit-note">{t("invoices.creditNote")}</Button>}
          {!readOnly && inv.can_void && <Button variant="ghost" className="text-red-600" onClick={() => setDlg("void")} data-testid="invoice-void">{t("invoices.void")}</Button>}
        </div>
      </div>
      {inv.status === "void" && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-900" data-testid="invoice-void-reason">{t("invoices.voidedBecause", { reason: inv.void_reason })}</p>}
      {inv.status !== "draft" && <p className="text-xs text-slate-500">{t("invoices.immutable")}</p>}
      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          {inv.status === "draft" && !readOnly && <DraftEditor key={inv.id} inv={inv} onSaved={reload} />}
          <QuoteDocument quote={inv} garage={{ ...garage, ...ident, contact: ident.contact || garage.contact }} title={t(`invoices.titles.${inv.title.replace(" ", "_").toLowerCase()}`)} meta={meta}>
            {isCN && inv.reason && <p className="text-sm" data-testid="credit-reason-text"><strong>{t("invoices.creditReason")}:</strong> {inv.reason}</p>}
          </QuoteDocument>
        </div>
        <div className="space-y-6">
          {!isCN && inv.status !== "draft" && <PaymentsCard inv={inv} />}
          <Card testId="invoice-log"><SectionTitle>{t("jobs.log")}</SectionTitle>
            <ol className="space-y-2 text-xs">{inv.log.map((e, i) => <li key={i} className="border-l-2 border-slate-200 pl-3"><p className="font-semibold">{t(`invoices.actions.${e.action}`)}{e.details?.number ? ` ${e.details.number}` : ""}{e.details?.amount_cents ? ` · ${fmtCents(e.details.amount_cents)}` : ""}</p><p className="text-slate-400">{e.by} · {fmtDateTime(e.at, garage.timezone)}</p></li>)}</ol>
          </Card>
        </div>
      </div>
      {dlg === "payment" && <PaymentDialog inv={inv} onClose={() => setDlg(null)} onDone={done} />}
      {dlg === "refund" && <PaymentDialog inv={inv} mode="refund" onClose={() => setDlg(null)} onDone={done} />}
      {dlg === "credit" && <CreditNoteDialog inv={inv} onClose={() => setDlg(null)} onDone={done} />}
      {dlg === "void" && <VoidDialog inv={inv} onClose={() => setDlg(null)} onDone={done} />}
      <ConfirmDialog open={confirmIssue} onOpenChange={setConfirmIssue} title={t("invoices.issueTitle")} description={t("invoices.issueText")} confirmLabel={t("invoices.issue")} onConfirm={issue} testId="invoice-issue-dialog" />
    </div>
  );
}

export default function InvoiceDetail() {
  return <PlanGate feature="quotes_and_invoices" managers><Detail /></PlanGate>;
}
